import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth, hasRole } from "@/lib/auth";
import { Layers, ChevronRight, Plus, CheckCircle2, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function Frameworks() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const isAdmin = hasRole(user, "tenant_admin", "hr_admin");

  useEffect(() => {
    api.get("/frameworks").then((r) => setItems(r.data.items || [])).finally(() => setLoading(false));
  }, []);

  const createBlank = async () => {
    setCreating(true);
    try {
      const r = await api.post("/frameworks", {
        name: "Untitled framework",
        description: "",
        objective_fields: [
          { id: "statement", label: "Objective statement", type: "textarea", required: true, help_text: "" },
        ],
        rating_scale: {
          type: "numeric",
          levels: [
            { value: 1, label: "Below", description: "" },
            { value: 3, label: "Meets", description: "" },
            { value: 5, label: "Outstanding", description: "" },
          ],
        },
        values_assessment: { enabled: false, values: [] },
        cycle_phases: [
          { id: "goal_setting", label: "Goal Setting", type: "goal_setting", default_window_days: 14, required: true },
        ],
        weightage_rules: { must_sum_to_100: true, min_objectives: 1, max_objectives: 8, per_objective_min_weight: 5 },
        final_rating_composition: { objective_weight_pct: 100, values_weight_pct: 0 },
      });
      toast.success("Draft framework created");
      navigate(`/frameworks/${r.data.id}/edit`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="max-w-6xl space-y-6" data-testid="frameworks-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="axia-overline">Library</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mt-1.5">Frameworks</h1>
          <p className="text-sm text-slate-500 mt-2 max-w-xl">
            The blueprint every cycle runs on. Pick a template — Balanced Scorecard, SMART+Values, or OKR — clone it to fit your operating model.
          </p>
        </div>
        {isAdmin && (
          <Button onClick={createBlank} disabled={creating} className="bg-slate-900" data-testid="new-framework-btn">
            <Plus className="w-3.5 h-3.5 mr-2" /> New framework
          </Button>
        )}
      </div>

      {loading ? (
        <div className="text-sm text-slate-500">Loading…</div>
      ) : items.length === 0 ? (
        <div className="axia-card p-12 text-center text-slate-500">
          <Layers className="w-6 h-6 mx-auto text-slate-300" />
          <div className="font-display text-lg font-semibold text-slate-900 mt-3">No frameworks yet</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {items.map((f) => (
            <Link
              key={f.id}
              to={`/frameworks/${f.id}`}
              className="axia-card p-6 hover:border-slate-400 transition group"
              data-testid={`framework-card-${f.id}`}
            >
              <div className="flex items-start justify-between">
                <div className="w-9 h-9 rounded-md bg-slate-900 text-white flex items-center justify-center">
                  <Layers className="w-4 h-4" />
                </div>
                <span
                  className={`axia-pill ${
                    f.status === "published"
                      ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                      : f.status === "draft"
                      ? "bg-amber-50 text-amber-700 border-amber-200"
                      : "bg-slate-100 text-slate-600 border-slate-200"
                  }`}
                >
                  {f.status === "published" ? (
                    <CheckCircle2 className="w-3 h-3" />
                  ) : f.status === "draft" ? null : (
                    <Lock className="w-3 h-3" />
                  )}
                  {f.status}
                </span>
              </div>
              <h3 className="font-display text-lg font-semibold text-slate-900 mt-4 leading-tight">{f.name}</h3>
              <p className="text-xs text-slate-500 mt-1.5 line-clamp-2 min-h-[2.4em]">{f.description}</p>
              <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
                <span>
                  {f.objective_fields?.length || 0} fields ·{" "}
                  {f.values_assessment?.enabled ? `${f.values_assessment.values.length} values` : "no values"}
                </span>
                <span className="font-mono inline-flex items-center gap-1 text-slate-700 group-hover:text-slate-900">
                  view <ChevronRight className="w-3 h-3" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
